import { useMemo, useState } from 'react'
import { Card, Table, Button, Badge, Spinner } from 'react-bootstrap'
import Select from 'react-select'
import { toast } from 'react-toastify'
import { useAdminMunicipalityOptions } from 'api/hooks/use-admin-municipalities'
import {
  useCreateAdminUserMunicipality,
  useDeleteAdminUserMunicipality
} from 'api/hooks/use-admin-user-municipalities'
import { errorMessage } from 'admin/shared/errors'

// The municipalities an accounting user may switch between. The one they are
// currently in can't be removed — the API refuses it too.
const UserMunicipalitiesManager = ({ user }) => {
  const userId = user?.id
  const memberships = user?.user_municipalities ?? []
  const [selected, setSelected] = useState(null)

  const { data: municipalityOptions = [], isLoading: loadingOptions } =
    useAdminMunicipalityOptions()

  const options = useMemo(() => {
    const assigned = new Set(memberships.map((m) => m.municipality_id))
    return municipalityOptions
      .filter((m) => !assigned.has(m.id))
      .map((m) => ({ value: m.id, label: m.name }))
  }, [municipalityOptions, memberships])

  const { mutate: addMunicipality, isLoading: adding } = useCreateAdminUserMunicipality({
    userId,
    options: {
      onSuccess: () => {
        setSelected(null)
        toast.success('Municipality added')
      },
      onError: (err) => toast.error(errorMessage(err, 'Failed to add municipality'))
    }
  })

  const { mutate: removeMunicipality, isLoading: removing } = useDeleteAdminUserMunicipality({
    userId,
    options: {
      onSuccess: () => toast.success('Municipality removed'),
      onError: (err) => toast.error(errorMessage(err, 'Failed to remove municipality'))
    }
  })

  return (
    <Card className="mb-4">
      <Card.Header><h5 className="mb-0">Municipalities</h5></Card.Header>
      <Card.Body>
        <p className="text-muted small mb-3">
          This accounting user can switch between these municipalities.
        </p>
        <Table bordered hover responsive className="mb-3">
          <thead>
            <tr>
              <th>Municipality</th>
              <th className="text-center" style={{ width: '8rem' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {memberships.map((membership) => {
              const isCurrent = membership.municipality_id === user?.municipality_id
              return (
                <tr key={membership.id}>
                  <td>
                    {membership.municipality_name}
                    {isCurrent && (
                      <Badge bg="light" text="primary" className="ms-2">Current</Badge>
                    )}
                  </td>
                  <td className="text-center">
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger p-0"
                      aria-label={`Remove ${membership.municipality_name}`}
                      disabled={isCurrent || removing}
                      onClick={() => removeMunicipality(membership.id)}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
        <div className="d-flex gap-2 align-items-center">
          <div className="flex-grow-1">
            <Select
              inputId="user-municipality-select"
              options={options}
              value={selected}
              onChange={setSelected}
              isLoading={loadingOptions}
              isClearable
              placeholder="Add a municipality..."
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </div>
          <Button
            variant="primary"
            disabled={!selected || adding}
            onClick={() => addMunicipality(selected.value)}
          >
            {adding ? <Spinner animation="border" size="sm" /> : 'Add'}
          </Button>
        </div>
      </Card.Body>
    </Card>
  )
}

export default UserMunicipalitiesManager
